import * as React from 'react'
import * as LodashFP from 'lodash/fp'

import { toast } from 'react-toastify'

import FormFieldComponent, { ValidationElement } from './FormField'
import Centered from '../../components/Centered'
import { id } from '../../utils/fp'
import {
  initialize,
  getFieldValidation,
  getFieldGuidelines,
  isFieldEmpty,
} from './helpers'
import {
  FormBlueprint,
  FormValue,
  FormFieldBlueprint,
  FormFieldNestedForm,
  FormFieldList,
  IFormValidation,
  FieldValidation,
  FieldGuidelinesType,
  FormGuidelinesType,
} from '../../interfaces/components/dynamic-form'
import { IStringAnyMap } from '../../types/map'
import {
  stringPath,
  KeyPath,
  combinePaths,
  pathForClassName as pathForClassNameFn,
} from '../../utils/object'
import {
  Typography,
  Button,
  Icon,
  IconButton,
  Tabs,
  Tab,
  AppBar,
  Paper,
  InputLabel,
  styled,
} from '@design/pt-ui'
import ErrorMessage from '../../components/ErrorMessage'
import { translateWithUserLocale } from '../../utils/i18n'
import { Creative } from '../../interfaces/entities/creative'

// prettier-ignore
const FORM_BACKGROUND_COLORS = [
  '#ffffff', // White as the KKK
  '#fafdff', // Light blue
  '#fdf1ff', // Light purple
]

const FieldIcon = styled(Icon)(({ theme }) => ({
  fontSize: 35,
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
}))

const StatusIcon = styled(Icon, {
  shouldForwardProp: (prop) => prop !== 'tone',
})<{ tone: 'check' | 'warning' | 'error' }>(({ theme, tone }) => ({
  fontSize: 35,
  marginRight: theme.spacing(1),
  marginLeft: theme.spacing(1),
  color: tone === 'check' ? '#57ac40' : tone === 'warning' ? '#ffb37a' : 'red',
}))

const Wrapper = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const LabelRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.dark,
}))

const StyledTab = styled(Tab)({
  flexGrow: 1,
  backgroundColor: '#fff',
  maxWidth: '10%',
  border: '1px solid #e8e8e8',
  minWidth: 160,
})

const TabPaper = styled(Paper)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
  borderRadius: 0,
}))

const DynamicForm = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(2),
}))

const IconRow = styled('div')({
  display: 'flex',
  flexFlow: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
})

type FormContextType = {
  update: (path: string, pending: boolean) => void
  isPending: boolean
}

export const FormContext = React.createContext<FormContextType>({
  update: () => {},
  isPending: false,
})

interface IFormComponentListProps {
  value: Array<any>
  of: FormFieldBlueprint
  formBlueprint: FormBlueprint
  path: KeyPath
  label: string
  validation: IFormValidation
  guidelines: FormGuidelinesType
  nestingLevel: number
  increaseNesting: boolean
  disabledOptionTooltip?: string
  locked?: boolean
  onChange: (value: Array<any>) => any
  creativeId?: string
  creative?: any
  autoAddElementIfEmpty?: boolean
  minimumElements?: number
  maximumElements?: number
  platform: string
}

const swapInArray = (a: any[], i: number, j: number) => {
  if (i > j) [i, j] = [j, i]

  return [...a.slice(0, i), a[j], ...a.slice(i + 1, j), a[i], ...a.slice(j + 1)]
}

type ListTab = number | 'new'
interface IFormComponentListState {
  index: ListTab
}

function FormComponentList(props: IFormComponentListProps) {
  let pendingIndexUpdate = React.useRef<number | null>(null)
  const pathForClassName = pathForClassNameFn(props.path)

  const prevProps = React.useRef<IFormComponentListProps | null>(null)

  const [state, setState] = React.useState<IFormComponentListState>({
    index: props.value.length > 0 ? 0 : 'new',
  })

  const context = React.useContext(FormContext)

  const addNew = () => {
    // We don't allow creating past maximum elements
    if (!canAdd()) {
      toast.warn(
        translateWithUserLocale('warning.dynamic_form.maximum_elements', {
          number: props.maximumElements,
        }),
        { toastId: 'max-elements-warn' },
      )
      return
    }

    props.onChange([...props.value, initialize(props.of)])
  }

  React.useEffect(() => {
    if (props.value.length === 0 && props.autoAddElementIfEmpty) {
      addNew()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (prevProps.current === null) {
      prevProps.current = props
      return
    }

    // Did we move something around?
    if (pendingIndexUpdate.current != null) {
      const newIndex = pendingIndexUpdate.current
      pendingIndexUpdate.current = null

      return setState((state) => ({ ...state, index: newIndex }))
    }

    if (state.index === 'new') {
      if (props.value.length > 0) {
        setState((state) => ({ ...state, index: props.value.length - 1 }))
      }
    } else {
      // Did we delete item we were on?
      if (props.value.length > 0 && state.index > props.value.length - 1) {
        setState((state) => ({ ...state, index: props.value.length - 1 }))
      } else {
        // Did we delete last item?
        if (props.value.length === 0) {
          setState((state) => ({ ...state, index: 'new' }))
        }

        // Did we add a new item?
        if (props.value.length === prevProps.current.value.length + 1) {
          setState((state) => ({
            ...state,
            index: props.value.length - 1,
          }))
        }
      }
    }

    prevProps.current = props
  }, [props.value]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleTabChange = (_event: any, tab: ListTab) => {
    if (typeof tab === 'number') {
      setState((state) => ({
        ...state,
        index: tab,
      }))
    }

    if (isDisabled()) {
      return
    }

    if (tab === 'new') {
      addNew()
    }
  }

  const canDelete = () => {
    if (props.minimumElements) {
      return props.value.length > props.minimumElements
    }

    return true
  }

  const canAdd = () => {
    if (props.maximumElements) {
      return props.value.length < props.maximumElements
    }

    return true
  }

  const addNewByDuplicatingCurrent = () => {
    // We don't allow creating past maximum elements
    if (!canAdd()) {
      toast.warn(
        translateWithUserLocale('warning.dynamic_form.maximum_elements', {
          number: props.maximumElements,
        }),
        { toastId: 'max-elements-warn' },
      )
      return
    }
    const currentValue = getCurrentValue()
    const blueprint = props.of
    const clone =
      blueprint.type === 'fieldset'
        ? LodashFP.pick(
            blueprint.fields.map((field) => field.name),
            LodashFP.cloneDeep(currentValue),
          )
        : LodashFP.cloneDeep(currentValue)
    props.onChange([...props.value, clone])
  }

  const handleValueRemoval = (index: number) => {
    if (isDisabled()) {
      return
    }

    // We don't allow deleting past minimum elements
    if (!canDelete()) {
      toast.warn(
        translateWithUserLocale('warning.dynamic_form.minimum_elements', {
          number: props.minimumElements,
        }),
        { toastId: 'min-elements-warn' },
      )

      return
    }

    const newValue = [...props.value]
    newValue.splice(index, 1)

    props.onChange(newValue)
  }

  const handleValueIndexMove = (currentIndex: number, wantedIndex: number) => {
    const maxIndex = props.value.length - 1
    if (
      isDisabled() ||
      currentIndex > maxIndex ||
      wantedIndex > maxIndex ||
      currentIndex < 0 ||
      wantedIndex < 0 ||
      currentIndex === wantedIndex
    ) {
      return
    }

    if (state.index === currentIndex) {
      pendingIndexUpdate.current = wantedIndex
    }

    const newValue = swapInArray([...props.value], currentIndex, wantedIndex)
    props.onChange(newValue)
  }

  const handleValueChange = (value: any) => {
    if (isDisabled() || state.index === 'new') {
      return
    }
    const newValue: any[] = [...props.value]
    newValue[state.index] = value

    props.onChange(newValue)
  }

  const getCurrentElementValidation: () => FieldValidation = () => {
    return getFieldValidation(
      stringPath(props.path) + `[${state.index}]`,
      props.validation,
    )
  }

  const getCurrentElementGuidelines: () => FieldGuidelinesType = () => {
    return getFieldGuidelines(
      stringPath(props.path) + `[${state.index}]`,
      props.guidelines,
    )
  }

  const getCurrentValue = () => {
    return LodashFP.getOr(null, ['value', state.index], props)
  }

  const arrowStyle = (active: boolean) => ({
    backgroundColor: active ? 'rgba(0,0,0,0.1)' : 'rgba(0,0,0,0.02)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100%',
    width: 35,
  })

  const renderMoveLeftButton = (index: number) => {
    const empty = index === 0
    const onClick = (event: React.SyntheticEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (empty) {
        return
      }

      handleValueIndexMove(index, index - 1)
    }

    return (
      <span
        className="cp-dynamic-form-list-move-left"
        style={{ ...arrowStyle(!empty), position: 'absolute', left: 0 }}
        onClick={onClick}>
        <FieldIcon>arrow_left</FieldIcon>
      </span>
    )
  }

  const renderMoveRightButton = (index: number) => {
    const empty = index === props.value.length - 1

    const onClick = (event: React.SyntheticEvent) => {
      event.preventDefault()
      event.stopPropagation()

      if (empty) {
        return
      }

      handleValueIndexMove(index, index + 1)
    }

    return (
      <span
        className="cp-dynamic-form-list-move-right"
        style={{ ...arrowStyle(!empty), position: 'absolute', right: 0 }}
        onClick={onClick}>
        <FieldIcon>arrow_right</FieldIcon>
      </span>
    )
  }

  const isDisabled = () => {
    return validUntil <= 0 || props.of.step > validUntil
  }
  const validUntil = Number(props.validation.validUntil)
  const topLevelValidation = getFieldValidation(props.path, props.validation)
  const elementValidation = getCurrentElementValidation()
  const backgroundColor =
    FORM_BACKGROUND_COLORS[props.nestingLevel % FORM_BACKGROUND_COLORS.length]

  const actionButtonsAndValidation = (
    <div
      style={{
        backgroundColor,
        border: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingLeft: 15,
        paddingTop: 15,
        paddingRight: 15,
      }}>
      <ValidationElement validation={elementValidation} />

      <div className="array-buttons">
        <Button
          className="cp-dynamic-form-list-duplicate"
          variant="outlined"
          color="inherit"
          onClick={addNewByDuplicatingCurrent}
          disabled={isDisabled()}>
          <FieldIcon>file_copy</FieldIcon>{' '}
          {translateWithUserLocale('duplicate')}
        </Button>

        <span style={{ marginLeft: 5 }} />

        <Button
          className="cp-dynamic-form-list-delete"
          variant="outlined"
          color="inherit"
          disabled={isDisabled() || !canDelete()}
          onClick={(event: React.SyntheticEvent) => {
            event.preventDefault()
            event.stopPropagation()
            if (state.index !== 'new') {
              handleValueRemoval(state.index)
            }
          }}>
          <Icon>delete</Icon> {translateWithUserLocale('delete')}
        </Button>
      </div>
    </div>
  )

  return (
    <Wrapper>
      <LabelRow>
        <InputLabel>{props.label || ''}</InputLabel>
        <ValidationElement validation={topLevelValidation} />
      </LabelRow>

      <Paper sx={{ backgroundColor, position: 'relative' }}>
        <AppBar
          position="static"
          color="default"
          sx={{ '& .MuiAppBar-positionStatic': { transform: 'none' } }}>
          <Tabs
            sx={{ backgroundColor, flexGrow: 1 }}
            value={props.value.length > 0 ? state.index : 'new'}
            onChange={handleTabChange}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto">
            {props.value.map((_val: FieldValidation, index: number) => {
              const elemPath = stringPath(props.path) + `[${index}]`
              let validation = getFieldValidation(elemPath, props.validation)

              const icon =
                validation.filter((msg) => msg.status === 'error').length >
                0 ? (
                  <StatusIcon tone="error">warning</StatusIcon>
                ) : validation.filter((msg) => msg.status === 'warning')
                    .length > 0 ? (
                  <StatusIcon tone="warning">warning</StatusIcon>
                ) : (
                  <StatusIcon tone="check">check</StatusIcon>
                )

              return (
                <StyledTab
                  key={index}
                  label={
                    <Centered
                      className={`cp-dynamic-form-list-element cp-dynamic-form-list-element-path@${pathForClassName}`}>
                      {renderMoveLeftButton(index)}
                      {icon}{' '}
                      <span className={`focus-${elemPath} focus-${index}`}>
                        {index + 1}
                      </span>
                      {renderMoveRightButton(index)}
                    </Centered>
                  }
                  sx={{ backgroundColor }}
                />
              )
            })}
            .concat(
            <StyledTab
              key="new"
              value="new"
              label={<Icon>add</Icon>}
              sx={{ backgroundColor }}
            />
            )
          </Tabs>
          {actionButtonsAndValidation}
        </AppBar>

        {props.value.length > 0 &&
        state.index !== 'new' &&
        state.index < props.value.length ? (
          props.of.type === 'fieldset' ? (
            <FormComponent
              key={state.index}
              index={state.index}
              blueprint={props.of}
              label={''}
              value={getCurrentValue()}
              path={stringPath(props.path) + `[${state.index}]`}
              validation={props.validation}
              guidelines={props.guidelines}
              locked={props.locked}
              onChange={handleValueChange}
              creativeId={props.creativeId}
              creative={props.creative}
              nestingLevel={props.nestingLevel}
              colorizeNesting={props.increaseNesting}
              paddingTop={1}
              autoAddToEmptyArrays={props.autoAddElementIfEmpty || false}
              platform={props.platform}
            />
          ) : props.of.type === 'list' ? (
            <FormComponentList
              key={state.index}
              path={props.path}
              label={props.of.label}
              value={getCurrentValue()}
              validation={props.validation}
              formBlueprint={props.formBlueprint}
              guidelines={props.guidelines}
              locked={props.locked}
              of={props.of.meta.of}
              onChange={handleValueChange}
              creativeId={props.creativeId}
              creative={props.creative}
              nestingLevel={
                props.increaseNesting
                  ? props.nestingLevel + 1
                  : props.nestingLevel
              }
              increaseNesting={props.increaseNesting}
              minimumElements={props.of.meta.minimumElements}
              maximumElements={props.of.meta.maximumElements}
              platform={props.platform}
            />
          ) : (
            <FormFieldComponent
              key={state.index}
              path={stringPath(props.path) + `[${state.index}]`}
              onPendingChange={(pending) => {
                context.update(
                  stringPath(props.path) + `[${state.index}]`,
                  pending,
                )
              }}
              blueprint={props.of}
              formBlueprint={props.formBlueprint}
              disabled={validUntil < props.of.step}
              collapsable={props.of.collapsable}
              disabledOptionTooltip={props.disabledOptionTooltip}
              value={getCurrentValue()}
              validation={getCurrentElementValidation()}
              guidelines={getCurrentElementGuidelines()}
              locked={props.locked}
              onChange={handleValueChange}
              creativeId={props.creativeId}
              creative={props.creative}
              platform={props.platform}
            />
          )
        ) : null}
      </Paper>
    </Wrapper>
  )
}

interface INestedFormComponentProps {
  blueprint: FormFieldNestedForm
  disabledOptionTooltip?: string
  value: FormValue
  path: KeyPath
  validation: IFormValidation
  guidelines: FormGuidelinesType
  disabledFields?: DisabledFields
  colorizeNesting: boolean
  locked?: boolean
  creativeId?: string
  creative?: Creative
  autoAddToEmptyArrays: boolean
  nestingLevel: number
  platform: string

  onPendingValidationChange?: (pending: boolean) => void
  onChange: (value: any, field: string) => any
}

function NestedFormComponent(props: INestedFormComponentProps) {
  const handleChange = (value: any) => {
    props.onChange(value, props.blueprint.name)
  }

  return (
    <FormComponent
      key={props.blueprint.name}
      path={props.path}
      disabledFields={props.disabledFields}
      blueprint={props.blueprint}
      label={props.blueprint.label}
      value={props.value}
      validation={props.validation}
      guidelines={props.guidelines}
      locked={props.locked}
      creativeId={props.creativeId}
      creative={props.creative}
      disabledOptionTooltip={props.disabledOptionTooltip}
      autoAddToEmptyArrays={props.autoAddToEmptyArrays}
      nestingLevel={props.nestingLevel}
      colorizeNesting={props.colorizeNesting}
      onChange={handleChange}
      platform={props.platform}
    />
  )
}

interface INestedFormListComponentProps {
  blueprint: FormFieldList
  formBlueprint: FormBlueprint
  path: KeyPath
  value: any[]
  validation: IFormValidation
  guidelines: FormGuidelinesType
  disabledOptionTooltip?: string
  locked?: boolean
  onChange: (value: any, field: string) => any
  creativeId?: string
  creative?: Creative
  autoAddElementIfEmpty?: boolean
  nestingLevel: number
  increaseNesting: boolean
  minimumElements?: number
  maximumElements?: number
  platform: string
}

function NestedFormListComponent(props: INestedFormListComponentProps) {
  const handleChange = (value: any) => {
    props.onChange(value, props.blueprint.name)
  }

  return (
    <FormComponentList
      path={props.path}
      key={props.blueprint.name}
      of={props.blueprint.meta.of}
      label={props.blueprint.label}
      disabledOptionTooltip={props.disabledOptionTooltip}
      value={props.value}
      validation={props.validation}
      formBlueprint={props.formBlueprint}
      guidelines={props.guidelines}
      locked={props.locked}
      onChange={handleChange}
      creativeId={props.creativeId}
      creative={props.creative}
      autoAddElementIfEmpty={props.autoAddElementIfEmpty}
      nestingLevel={props.nestingLevel}
      increaseNesting={props.increaseNesting}
      minimumElements={props.minimumElements}
      maximumElements={props.maximumElements}
      platform={props.platform}
    />
  )
}

export type DisabledFields =
  | {
      kind: 'only'
      fields: KeyPath[]
    }
  | {
      kind: 'except'
      fields: KeyPath[]
    }

export function isFieldDisabled(
  disabledProps: DisabledFields | undefined,
  ipath: KeyPath,
) {
  if (disabledProps === undefined) {
    return false
  }
  const path = stringPath(ipath)
  const fields = disabledProps.fields.map((field) => stringPath(field))

  if (disabledProps.kind === 'except') {
    return !fields.includes(path)
  } else {
    return fields.includes(path)
  }
}

interface IFormComponentProps {
  blueprint: FormBlueprint
  value: FormValue
  validation: IFormValidation
  guidelines: FormGuidelinesType
  disabledFields?: DisabledFields
  colorizeNesting: boolean
  nestingLevel?: number
  disabledOptionTooltip?: string
  autoAddToEmptyArrays: boolean
  label: string | React.ReactNode
  path?: KeyPath
  locked?: boolean
  creativeId?: string
  creative?: Creative
  paddingTop?: number
  onPendingValidationChange?: (pendingValidation: boolean) => void
  onChange: (value: any) => any
  onClose?: () => any
  platform: string
  index?: number
}

function FormComponent(props: IFormComponentProps) {
  const handleChange = (value: IStringAnyMap, field: string) => {
    if (props.locked) {
      return
    }

    const newValue = {
      ...props.value,
      [field]: value,
    }
    props.onChange(newValue)
  }

  const handleClose = () => {
    if (props.onClose) {
      props.onClose()
    }
  }

  const { path = [], nestingLevel = 0, paddingTop } = props
  const fieldNames = props.blueprint.fields.map((b) => b.name)
  const valueFields = Object.keys(props.value || {})
  const hasLabel = props.label != null && props.label !== ''

  const sortedFieldNames = LodashFP.sortBy(id, fieldNames)
  const sortedValueFields = LodashFP.sortBy(id, valueFields)

  const missingFields = !LodashFP.isEqual(sortedFieldNames, sortedValueFields)
    ? sortedFieldNames.filter((x) => !sortedValueFields.includes(x))
    : []

  const backgroundColor =
    FORM_BACKGROUND_COLORS[nestingLevel % FORM_BACKGROUND_COLORS.length]

  const context = React.useContext(FormContext)
  const { onPendingValidationChange } = props

  React.useEffect(() => {
    if (onPendingValidationChange) {
      onPendingValidationChange(context.isPending)
    }
  }, [context.isPending]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <DynamicForm>
      {missingFields.length > 0 ? (
        <TabPaper sx={{ backgroundColor, paddingTop }}>
          <InputLabel>{props.label}</InputLabel>
          <ErrorMessage>
            Not all values for form present. Missing: {missingFields.join(', ')}
          </ErrorMessage>
        </TabPaper>
      ) : null}
      {!LodashFP.isEqual(LodashFP.uniq(fieldNames), fieldNames) ? (
        <TabPaper>
          <InputLabel>{props.label}</InputLabel>
          <ErrorMessage>
            Form has duplicate fields. Duplicated:
            {LodashFP.uniq(
              fieldNames.filter(
                (i) => fieldNames.filter((ii) => ii === i).length > 1,
              ),
            ).join(', ')}
          </ErrorMessage>
        </TabPaper>
      ) : null}
      <TabPaper sx={{ backgroundColor, paddingTop }}>
        {hasLabel || props.onClose ? (
          <>
            {props.platform !== 'facebook' ? (
              <IconRow>
                <Typography variant="subtitle1">{props.label}</Typography>

                {props.onClose ? (
                  <IconButton
                    onClick={handleClose}
                    sx={{ color: 'primary.main' }}>
                    <Icon>close</Icon>
                  </IconButton>
                ) : null}
              </IconRow>
            ) : (
              <div>
                <Typography variant="subtitle1">{props.label}</Typography>

                {props.onClose ? (
                  <IconButton
                    onClick={handleClose}
                    sx={{ color: 'primary.main' }}>
                    <Icon>close</Icon>
                  </IconButton>
                ) : null}
              </div>
            )}
          </>
        ) : null}

        {props.blueprint.fields
          .filter((f) => !f.hidden)
          .map((b) => {
            const value = LodashFP.getOr(null, ['value', b.name], props)
            const combinedPath = combinePaths(path, b.name)
            const validation = getFieldValidation(
              combinedPath,
              props.validation,
            )
            const guidelines = getFieldGuidelines(
              combinedPath,
              props.guidelines,
            )
            const explicitelyLocked = isFieldDisabled(
              props.disabledFields,
              combinedPath,
            )

            if (b.type === 'fieldset') {
              return (
                <NestedFormComponent
                  key={b.name}
                  path={combinedPath}
                  disabledFields={props.disabledFields}
                  blueprint={b}
                  value={value}
                  disabledOptionTooltip={props.disabledOptionTooltip}
                  validation={props.validation}
                  guidelines={props.guidelines}
                  locked={explicitelyLocked || props.locked}
                  creativeId={props.creativeId}
                  creative={props.creative}
                  onChange={handleChange}
                  onPendingValidationChange={props.onPendingValidationChange}
                  nestingLevel={
                    props.colorizeNesting ? nestingLevel + 1 : nestingLevel
                  }
                  colorizeNesting={props.colorizeNesting}
                  autoAddToEmptyArrays={props.autoAddToEmptyArrays}
                  platform={props.platform}
                />
              )
            }

            if (b.type === 'list') {
              return (
                <NestedFormListComponent
                  key={b.name}
                  path={combinedPath}
                  value={value}
                  disabledOptionTooltip={props.disabledOptionTooltip}
                  validation={props.validation}
                  guidelines={props.guidelines}
                  locked={explicitelyLocked || props.locked}
                  blueprint={b}
                  formBlueprint={props.blueprint}
                  creativeId={props.creativeId}
                  creative={props.creative}
                  onChange={handleChange}
                  autoAddElementIfEmpty={props.autoAddToEmptyArrays}
                  nestingLevel={nestingLevel + 1}
                  increaseNesting={props.colorizeNesting}
                  minimumElements={b.meta.minimumElements}
                  maximumElements={b.meta.maximumElements}
                  platform={props.platform}
                />
              )
            }

            return (
              <div key={b.name}>
                <FormFieldComponent
                  blueprint={b}
                  index={props.index}
                  formBlueprint={props.blueprint}
                  value={value}
                  disabled={
                    explicitelyLocked ||
                    Number(props.validation.validUntil) < b.step
                  }
                  collapsable={b.collapsable}
                  initiallyCollapsed={b.collapsable && isFieldEmpty(b, value)}
                  disabledOptionTooltip={props.disabledOptionTooltip}
                  validation={validation}
                  guidelines={guidelines}
                  locked={explicitelyLocked || props.locked}
                  path={combinedPath}
                  onPendingChange={(pending) => {
                    context.update(stringPath(combinedPath), pending)
                  }}
                  creativeId={props.creativeId}
                  creative={props.creative}
                  onChange={handleChange}
                  backgroundColor={backgroundColor}
                  platform={props.platform}
                />
              </div>
            )
          })}
      </TabPaper>
    </DynamicForm>
  )
}

type FormState = {
  pendingValidation: {
    [path: string]: boolean
  }
}

function MainForm(props: IFormComponentProps) {
  const [state, setState] = React.useState<FormState>({
    pendingValidation: {},
  })

  const updateForPath = (path: string, pending: boolean) => {
    setState((prevState) => ({
      ...prevState,
      pendingValidation: {
        ...prevState.pendingValidation,
        [path]: pending,
      },
    }))
  }

  let isPending = false
  for (const path of Object.keys(state.pendingValidation)) {
    if (state.pendingValidation[path]) {
      isPending = true
      break
    }
  }

  return (
    <FormContext.Provider value={{ update: updateForPath, isPending }}>
      <FormComponent {...props} />
    </FormContext.Provider>
  )
}

export default MainForm
